import { TuiElasticContainer } from '@taiga-ui/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { availableLocales } from '@locale/config/available-locales';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  TuiButton,
  TuiCell,
  TuiDialogContext,
  TuiNotification,
  TuiRadio,
  TuiScrollbar,
  TuiTitle,
} from '@taiga-ui/core';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n';
import { TuiFlagPipe } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { first, Observable } from 'rxjs';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';

@UntilDestroy()
@Component({
  selector: 'pxc-locale-picker-dialog',
  templateUrl: './locale-picker-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiButton,
    TuiFlagPipe,
    TranslocoDirective,
    TuiCell,
    TuiNotification,
    TuiRadio,
    TuiScrollbar,
    TuiElasticContainer,
    TuiTitle,
  ],
})
export class LocalePickerDialogComponent {
  private context = inject<
    TuiDialogContext<
      void,
      {
        label: string;
      }
    >
  >(POLYMORPHEUS_CONTEXT);
  private readonly authenticationService = inject(AuthenticationService);

  open = false;
  availableLocales = availableLocales;
  initialLocale = '';
  selectedLocaleId = '';

  private readonly switcher = inject(TuiLanguageSwitcherService);
  private readonly translocoService = inject(TranslocoService);

  readonly activeLanguage$: Observable<string> = this.translocoService.langChanges$;

  constructor() {
    const activeLang = this.translocoService.getActiveLang();
    this.initialLocale = activeLang;
    this.selectedLocaleId = activeLang;
  }

  confirmChangeLanguage(): void {
    const selectedLocale = availableLocales.find((lang) => lang.code === this.selectedLocaleId);
    const langKey = selectedLocale.code;
    if (this.selectedLocaleId !== this.initialLocale) {
      this.translocoService
        .load(langKey)
        .pipe(first())
        .subscribe(() => {
          this.updateLocale(langKey, selectedLocale.tuiConfig.languageName);
        });
    } else {
      this.context.completeWith();
    }
  }

  setSelectedLanguageId(languageId: string): void {
    this.selectedLocaleId = languageId;
  }

  updateLocale(langKey: string, tuiLanguageName: string): void {
    this.translocoService.setActiveLang(langKey);
    this.switcher.setLanguage(tuiLanguageName);
    this.authenticationService
      .updateCurrentUser({
        ...this.authenticationService.user,
        localeCode: langKey,
      })
      .subscribe({
        next: () => {
          this.context.completeWith();
          window.location.reload();
        },
        error: (error) => {
          console.error('Error updating locale:', error);
          this.context.completeWith();
        },
      });
  }
}
